import React, { ReactNode } from "react";
import { Layout, Button, theme } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import LanguageSwitcher from "../LanguageSwitcher";
import AppLogo from "../AppLogo";
import BackButton from "../BackButton";
import SiteFooter from "./SiteFooter";
import { useTranslation } from "react-i18next";
import { CORAL } from "../../theme/creamyTokens";
import { TRANSLATIONS } from "../../constants/translations";
import { translate } from "../../helpers/translationHelper";
import { useAppBreakpoint } from "../../hooks/useAppBreakpoint";

const { Header, Content, Footer } = Layout;

interface NotAuthenticatedLayoutProps {
  children: ReactNode;
}

/**
 * Header+footer chrome for the signed-out auth flow (Login, Register, and
 * Login's own 2FA step) -- same site-wide SiteFooter as LandingLayout
 * instead of a bare copyright line, plus a way back to the rest of the
 * site (logo, back arrow) that a login/register form has no business
 * hand-rolling itself.
 */
const NotAuthenticatedLayout: React.FC<NotAuthenticatedLayoutProps> = ({ children }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useAppBreakpoint();
  // The header's own CTA offers whichever of Login/Register the visitor
  // isn't already on -- showing "Log in" again while already on /login
  // would be a dead-end button.
  const onRegister = location.pathname.startsWith("/register");

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Full-width Fixed Header */}
      <Header
        style={{
          position: "fixed",
          width: "100%",
          zIndex: 1000,
          background: token.colorBgContainer,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 16px",
        }}
      >
        {/* Left: back to wherever the visitor came from, then the logo --
            same as LandingLayout's own header, so a signed-out visitor who
            landed here directly still has a way back to the site's entry
            point. */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <BackButton fallbackPath="/" />
          <AppLogo />
        </div>

        {/* Right: Language Switcher + the *other* auth action -- a
            secondary/navigational action off the current screen, not this
            screen's own primary submit, so it's an outlined coral pill
            rather than a solid `type="primary"`. */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <LanguageSwitcher />
          <Button
            style={{ borderRadius: 999, borderColor: CORAL, color: CORAL, background: "transparent" }}
            onClick={() => navigate(onRegister ? "/login" : "/register")}
          >
            {translate(t, onRegister ? TRANSLATIONS.LOG_IN : TRANSLATIONS.LAYOUT_HEADER_SIGN_UP)}
          </Button>
        </div>
      </Header>

      {/* Main Content */}
      <Layout>
        <Content
          style={{
            // Same reduced-margin criterion already applied to Explorar on
            // mobile -- desktop keeps its original roomier padding and
            // vertical centering (plenty of space there for it to read as
            // intentional rather than empty).
            padding: isMobile ? "8px" : "24px",
            marginTop: 64,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            // Top-aligned on mobile so the title sits right under the header
            // instead of vertically centered with a large empty gap above it
            // on a short form and a tall viewport. Column direction makes
            // justifyContent the vertical axis and alignItems the
            // horizontal one, so this pair is swapped from what it'd be
            // in a row layout.
            justifyContent: isMobile ? "flex-start" : "center",
            alignItems: "center",
            minHeight: isMobile ? undefined : "calc(100vh - 128px)",
            width: "100%", // Ensure full width
          }}
        >
          {children}
        </Content>
        <Footer>
          <SiteFooter />
        </Footer>
      </Layout>
    </Layout>
  );
};

export default NotAuthenticatedLayout;

